import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { adminApiError } from '../adminApi';
import { EvalBadge, EvalError } from '../evals/EvalBits';
import { shortId } from '../evals/format';
import {
  KAPPA_UNDEFINED_TITLE,
  adoptionText,
  agreementQuery,
  agreementQueryFromSearch,
  calibrationErrorText,
  confusionRows,
  directionText,
  formatAgreement,
  formatKappa,
  itemHref,
  judgeKey,
  judgeKeyText,
  reportField,
  reportStatusText,
  runCaseHref,
  setHref,
  type AgreementGroup,
  type CalibrationAgreementReport,
  type CalibrationJudgeAgreement,
  type ConfusionMatrix,
  type LabellerAgreement,
} from '../evals/labels';
import { labelsApi } from '../evals/labelsApi';
import { GuideLink } from '../evals/GuideLink';
import '../../styles/admin-eval-labels.css';

// The agreement report of one set version (TOM-141; ADR 0234 §4, §5 as
// amended by ADR 0252): per judge model and judge version, percent agreement,
// Cohen's kappa and the confusion table, the same per suite and category, and
// every disagreement linked to its item and to the run whose grading
// disagreed. Every figure counts only the items this admin has labelled (the
// server leaves the rest out and counts them in hiddenItems). The adoption
// rule gives no verdict until every item of the version is labelled.

function Kappa({ k }: { k: number | null }) {
  return <span title={k === null ? KAPPA_UNDEFINED_TITLE : undefined}>{formatKappa(k)}</span>;
}

function Confusion({
  m,
  field,
  testId,
}: {
  m: ConfusionMatrix;
  field: ReturnType<typeof reportField>;
  testId?: string;
}) {
  const rows = confusionRows(m, field);
  return (
    <div className="scroll-x">
      <table className="eval-label-confusion" data-testid={testId}>
        <caption className="admin-muted eval-sub">Judge down, label across</caption>
        <thead>
          <tr>
            <th scope="col" aria-label="Judge" />
            <th scope="col">
              {field === null ? 'Label yes' : `Label: ${rows[0]!.judge.toLowerCase()}`}
            </th>
            <th scope="col">
              {field === null ? 'Label no' : `Label: ${rows[1]!.judge.toLowerCase()}`}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.judge}>
              <th scope="row">Judge: {r.judge.toLowerCase()}</th>
              <td className={i === 0 ? 'is-agree' : 'is-disagree'}>{r.labelTrue}</td>
              <td className={i === 1 ? 'is-agree' : 'is-disagree'}>{r.labelFalse}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupTable({ title, groups }: { title: string; groups: AgreementGroup[] }) {
  if (groups.length === 0) return null;
  return (
    <div className="scroll-x">
      <table className="admin-table eval-table eval-table--compact">
        <thead>
          <tr>
            <th>{title}</th>
            <th>Items</th>
            <th>Agreement</th>
            <th>Kappa</th>
            <th>Judge yes / label no</th>
            <th>Judge no / label yes</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.key}>
              <td>
                <code>{g.key}</code>
              </td>
              <td>{g.matrix.total}</td>
              <td>{formatAgreement(g.percentAgreement)}</td>
              <td>
                <Kappa k={g.kappa} />
              </td>
              <td>{g.matrix.trueFalse}</td>
              <td>{g.matrix.falseTrue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JudgeCard({ setId, j }: { setId: string; j: CalibrationJudgeAgreement }) {
  const field = reportField(j.bySuite);
  return (
    <section className="eval-card" data-testid={`eval-agreement-judge-${judgeKey(j)}`}>
      <div className="eval-card-header">
        <h3>
          <code>{j.judgeModel}</code>{' '}
          <span className="admin-muted">@ {j.judgeVersion ?? 'no version'}</span>
        </h3>
        <span className="eval-sub admin-muted">
          {j.graded} labelled {j.graded === 1 ? 'item' : 'items'} graded
        </span>
      </div>
      <div className="eval-label-figures">
        <div>
          <span className="eval-label-figure" data-testid="eval-agreement-percent">
            {formatAgreement(j.percentAgreement)}
          </span>
          <span className="admin-muted eval-sub">agreement</span>
        </div>
        <div>
          <span className="eval-label-figure" data-testid="eval-agreement-kappa">
            <Kappa k={j.kappa} />
          </span>
          <span className="admin-muted eval-sub">Cohen&apos;s kappa</span>
        </div>
        <Confusion m={j.matrix} field={field} testId="eval-agreement-confusion" />
      </div>
      {field === null ? (
        <p className="admin-muted eval-sub">
          Yes is declined for a short-suite item and met for a long-suite one.
        </p>
      ) : null}
      <h4>By suite</h4>
      <GroupTable title="Suite" groups={j.bySuite} />
      <h4>By category</h4>
      <GroupTable title="Category or surface" groups={j.byCategory} />
      <h4>
        Disagreements <span className="admin-muted eval-sub">({j.disagreements.length})</span>
      </h4>
      {j.disagreements.length === 0 ? (
        <p className="admin-muted eval-sub">None: this judge agreed on every item counted.</p>
      ) : (
        <ul className="eval-label-disagreements" data-testid="eval-agreement-disagreements">
          {j.disagreements.map((d) => (
            <li key={`${d.itemId}|${d.gradedResultId}`}>
              <Link to={itemHref(setId, d.itemId)}>
                <code>{d.caseId}</code>
              </Link>{' '}
              <span className="admin-muted eval-sub">
                {d.category}
                {d.testSplit ? ' · test split' : ''}
              </span>
              <span className="eval-sub">{directionText(d.direction, d.suite)}</span>
              <span className="eval-sub">
                <Link
                  to={runCaseHref(d.gradedRunId, d.caseId)}
                  data-testid="eval-agreement-run-link"
                >
                  Run <code>{shortId(d.gradedRunId)}</code>
                </Link>
                {d.gradedResultId !== d.resultId ? (
                  <span className="admin-muted"> (a re-grade of the drawn reply)</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {j.extraGradings > 0 ? (
        <p className="admin-muted eval-sub">
          {j.extraGradings} further gradings by this judge were not counted: the first per item
          counts.
        </p>
      ) : null}
    </section>
  );
}

function Labellers({ rows }: { rows: LabellerAgreement[] }) {
  if (rows.length === 0) return null;
  return (
    <section className="eval-card">
      <h3>Between people</h3>
      <p className="admin-hint">
        How far another labeller agrees with the ground truth: a measure of how clear the rubric is,
        not of the judge.
      </p>
      <div className="scroll-x">
        <table className="admin-table eval-table eval-table--compact">
          <thead>
            <tr>
              <th>Labeller</th>
              <th>Both labelled</th>
              <th>Agreement</th>
              <th>Kappa</th>
              <th>Differ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.second}>
                <td>{r.second}</td>
                <td>{r.paired}</td>
                <td>{formatAgreement(r.percentAgreement)}</td>
                <td>
                  <Kappa k={r.kappa} />
                </td>
                <td>{r.disagreements.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Adoption({
  report,
  current,
  candidate,
  onApply,
}: {
  report: CalibrationAgreementReport;
  current: string;
  candidate: string;
  onApply: (current: string, candidate: string) => void;
}) {
  const keys = report.judges.map((j) => ({ key: judgeKey(j), text: judgeKeyText(j) }));
  const [cur, setCur] = useState(current || keys[0]?.key || '');
  const [cand, setCand] = useState(candidate || keys[1]?.key || '');

  function submit(e: FormEvent) {
    e.preventDefault();
    if (cur.length > 0 && cand.length > 0 && cur !== cand) onApply(cur, cand);
  }

  return (
    <section className="eval-card" data-testid="eval-agreement-adoption">
      <h3>The adoption rule</h3>
      <p className="admin-hint">
        Switch to a candidate judge only when its kappa on this set is at least the current
        judge&apos;s (percent agreement when every label is the same), both measured on every item
        with the same labels. The default judges are a setting, the{' '}
        <strong>Default eval judges</strong> on <Link to="/admin/models">Manage → Models</Link>.
        Saving a change there does not check agreement, so check it here first.
      </p>
      {keys.length < 2 ? (
        <p className="admin-muted eval-sub">
          Two judges must have graded this set&apos;s items to compare them. Re-grade the export
          with another judge (<code>pnpm neo:eval --from=&lt;export&gt; --judge=&lt;id&gt;</code>).
        </p>
      ) : (
        <form className="eval-filters" onSubmit={submit}>
          <label>
            Current judge
            <select
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              data-testid="eval-agreement-current"
            >
              {keys.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.text}
                </option>
              ))}
            </select>
          </label>
          <label>
            Candidate
            <select
              value={cand}
              onChange={(e) => setCand(e.target.value)}
              data-testid="eval-agreement-candidate"
            >
              {keys.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.text}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="admin-button-secondary"
            disabled={cur === cand}
            data-testid="eval-agreement-compare"
          >
            Compare
          </button>
        </form>
      )}
      {report.adoption !== null ? (
        <p
          className={report.adoption.decision === null ? 'eval-callout' : 'eval-label-decision'}
          data-testid="eval-agreement-decision"
        >
          {report.adoption.decision !== null ? (
            <EvalBadge tone={report.adoption.decision.adopt ? 'succeeded' : 'neutral'}>
              {report.adoption.decision.adopt ? 'Adopt' : 'Keep'}
            </EvalBadge>
          ) : null}{' '}
          {adoptionText(report.adoption)}
        </p>
      ) : null}
    </section>
  );
}

export function AdminEvalAgreement() {
  const { setId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const query = agreementQueryFromSearch(search);
  const queryKey = agreementQuery(query);
  const [report, setReport] = useState<CalibrationAgreementReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setError(null);
    labelsApi
      .agreement(setId, agreementQueryFromSearch(new URLSearchParams(queryKey)))
      .then((r) => {
        if (!ignore) setReport(r);
      })
      .catch((err) => {
        const body = adminApiError(err);
        if (!ignore) setError(calibrationErrorText(body.error, body.message));
      });
    return () => {
      ignore = true;
    };
  }, [setId, queryKey]);

  function apply(next: typeof query) {
    setSearch(new URLSearchParams(agreementQuery(next)));
  }

  return (
    <section className="admin-section eval-page">
      <p className="admin-hint">
        <Link to={setHref(setId)}>← The set</Link>
      </p>
      <header className="admin-section-header">
        <h1>
          Agreement report{' '}
          {report !== null ? (
            <span className="admin-muted">
              · set <code title={report.lineageId}>{shortId(report.lineageId)}</code> v
              {report.version}
            </span>
          ) : null}
        </h1>
        <GuideLink section="agreement" />
      </header>

      <EvalError message={error} />
      {report === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {report !== null ? (
        <>
          <p
            className={report.status === 'complete' ? 'eval-label-decision' : 'eval-callout'}
            data-testid="eval-agreement-status"
          >
            <EvalBadge tone={report.status === 'complete' ? 'succeeded' : 'warn'}>
              {report.status === 'complete' ? 'Complete' : 'Incomplete set'}
            </EvalBadge>{' '}
            {reportStatusText(report)}
          </p>
          {report.hiddenItems > 0 ? (
            <p className="admin-hint" data-testid="eval-agreement-hidden">
              {report.hiddenItems} {report.hiddenItems === 1 ? 'item' : 'items'} labelled by{' '}
              {report.labeller} that you have not labelled {report.hiddenItems === 1 ? 'is' : 'are'}{' '}
              left out of every figure, so nothing of an item&apos;s verdict reaches you before you
              label it.
            </p>
          ) : null}
          {report.labellers.length > 1 ? (
            <form className="eval-filters" aria-label="Ground truth">
              <label>
                Ground truth: whose labels
                <select
                  value={report.labeller ?? ''}
                  onChange={(e) => apply({ ...query, labeller: e.target.value })}
                  data-testid="eval-agreement-labeller"
                >
                  {report.labellers.map((l) => (
                    <option key={l.labeller} value={l.labeller}>
                      {l.labeller} ({l.labelled})
                    </option>
                  ))}
                </select>
              </label>
            </form>
          ) : null}

          {report.judges.length === 0 ? (
            <div className="admin-empty-state">
              <p className="admin-empty-state-title">Nothing to measure yet.</p>
              <p className="admin-hint">
                The figures count only items you have labelled. Label some items of this set, then
                come back.
              </p>
            </div>
          ) : (
            <div className="eval-cards eval-cards--stack" data-testid="eval-agreement-judges">
              {report.judges.map((j) => (
                <JudgeCard key={judgeKey(j)} setId={setId} j={j} />
              ))}
            </div>
          )}

          <div className="eval-cards eval-label-cards">
            <Adoption
              key={`${query.current ?? ''}|${query.candidate ?? ''}`}
              report={report}
              current={query.current ?? ''}
              candidate={query.candidate ?? ''}
              onApply={(current, candidate) => apply({ ...query, current, candidate })}
            />
            <Labellers rows={report.labellerAgreement} />
          </div>
        </>
      ) : null}
    </section>
  );
}
